import { ApolloProvider } from '@apollo/client';
import { AuthProvider, useSession, useUser } from '@descope/react-sdk';
import { LIGHT_THEME, Provider } from '@smithhealth/design-system';
import type { StatsigClient, StatsigPlugin } from '@statsig/js-client';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { useEffect, useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { DescopeWrappedApp } from '@components/descope-wrapper/descope-wrapper';
import { InactivityWarningModal } from '@components/inactivity-warning-modal/inactivity-warning-modal';
import { Layout } from '@components/layout/layout';
import { config } from '@config/index';
import { useLoggedInUser } from '@services/auth/use-logged-in-user';
import { client } from '@services/graphql-client';
import { PharmacyProvider } from '@services/pharmacy';
import {
  mapUserToStatsig,
  type TelemetryUser,
} from '@services/telemetry/map-user-to-statsig';
import './app.scss';
import { Documents } from './pages/documents/documents';
import { Home } from './pages/home/home';
import { MacList } from './pages/mac-list/mac-list';

const AUTO_CAPTURE_PLUGIN = new StatsigAutoCapturePlugin({
  consoleLogAutoCaptureSettings: {
    enabled: true,
  },
});

const AppContent = () => {
  const { user: descopeUser } = useUser();
  const { sessionToken } = useSession();
  const loggedInUser = useLoggedInUser();

  const telemetryUser: TelemetryUser | undefined = useMemo(() => {
    // Only provide user info if authenticated
    if (!sessionToken || !descopeUser?.userId) {
      return undefined;
    }

    return {
      descopeId: descopeUser.userId,
      email: loggedInUser.email,
      firstName: loggedInUser.firstName,
      lastName: loggedInUser.lastName,
      // If user is being impersonated, include actor information
      actorDescopeId: loggedInUser.isMimicking ? descopeUser.userId : undefined,
      actorEmail: loggedInUser.isMimicking ? descopeUser.email : undefined,
    };
  }, [
    sessionToken,
    descopeUser?.userId,
    descopeUser?.email,
    loggedInUser.email,
    loggedInUser.firstName,
    loggedInUser.lastName,
    loggedInUser.isMimicking,
  ]);

  const statsigUser = useMemo(
    () => mapUserToStatsig(telemetryUser),
    [telemetryUser]
  );

  // The async-init provider only captures the initial (unauthenticated) user, so
  // re-identify once the authenticated user resolves to associate events with them.
  const { client: statsigClient } = useClientAsyncInit(
    config.statsig.sdkKey,
    statsigUser,
    {
      environment: { tier: config.statsig.environment },
      networkConfig: {
        api: config.statsig.api,
        logEventUrl: config.statsig.eventLoggingApi
          ? `${config.statsig.eventLoggingApi}/event`
          : undefined,
      },
      //@ts-expect-error
      plugins: [AUTO_CAPTURE_PLUGIN] as StatsigPlugin<StatsigClient>[],
    }
  );

  useEffect(() => {
    void statsigClient.updateUserAsync(statsigUser);
  }, [statsigClient, statsigUser]);

  return (
    <StatsigProvider client={statsigClient}>
      <BrowserRouter>
        <DescopeWrappedApp config={config.descope.pharmacyPortal}>
          <PharmacyProvider>
            <Layout>
              <InactivityWarningModal />
              <Routes>
                <Route path="/" Component={Home} />
                <Route path="/mac" Component={MacList} />
                <Route path="/documents" Component={Documents} />
                <Route path="*" element={<div>404 Not Found</div>} />
              </Routes>
            </Layout>
          </PharmacyProvider>
        </DescopeWrappedApp>
      </BrowserRouter>
    </StatsigProvider>
  );
};

export const App = () => {
  return (
    <AuthProvider projectId={config.descope.projectId}>
      <ApolloProvider client={client}>
        <Provider {...LIGHT_THEME}>
          <AppContent />
        </Provider>
      </ApolloProvider>
    </AuthProvider>
  );
};
